import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea
} from 'recharts';
import { COLORS, CustomLegend } from './ChartUtils';

const TemperatureChart = ({ data, area }) => {
  const [activeData, setActiveData] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const chartRef = useRef(null);
  
  // Process the data and prepare it for the chart
  const processedData = useMemo(() => {
    if (!data?.temperature?.[area]) return [];
    
    return Object.entries(data.temperature[area]).map(([timestamp, values]) => {
      // Rename limit properties for consistency
      const dataPoint = { 
        timestamp,
        min_limit: values.limit_min,
        max_limit: values.limit_max
      };
      
      // Add all sensor values - preserve null values instead of defaulting to 0
      Object.entries(values).forEach(([key, value]) => {
        if (!['limit_min', 'limit_max'].includes(key)) {
          // Use null for missing or invalid data instead of defaulting to 0
          dataPoint[key] = (value === null || value === undefined) ? null : 
            (typeof value === 'number' ? value : parseFloat(value) || null);
        }
      });
      
      return dataPoint;
    });
  }, [data, area]);

  // Handle initial data and measure container width
  useEffect(() => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
    
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };
    
    const timer = setTimeout(updateWidth, 100);
    
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, [processedData]);

  // Chart interaction handlers
  const handleMouseMove = (props) => {
    if (props?.activePayload?.[0]?.payload) {
      setActiveData(props.activePayload[0].payload);
    }
  };

  const handleMouseLeave = () => {
    if (processedData.length > 0) {
      setActiveData(processedData[processedData.length - 1]);
    }
  };

  // Custom X-axis tick formatter
  const formatXAxis = (tickItem) => {
    if (!tickItem) return '';
    
    const dateParts = tickItem.split(' ');
    if (dateParts.length < 2) return tickItem;
    
    const timePart = dateParts[1];
    
    if (timePart === '00:00:00') {
      const [year, month, day] = dateParts[0].split('-');
      return `${day}.${month}`;
    }
    
    return timePart.split(':')[0];
  };

  // Create one tick per day
  const oneDayTicks = useMemo(() => {
    const ticks = [];
    const seenDates = new Set();
    
    processedData.forEach(dataPoint => {
      if (!dataPoint.timestamp) return;
      
      const [datePart] = dataPoint.timestamp.split(' ');
      
      if (!seenDates.has(datePart)) {
        seenDates.add(datePart);
        ticks.push(dataPoint.timestamp);
      }
    });
    
    return ticks;
  }, [processedData]);

  if (!processedData.length) return null;

  // Extract sensor keys for the legend and lines
  const sensorKeys = Object.keys(processedData[0])
    .filter(key => !['timestamp', 'min_limit', 'max_limit'].includes(key));

  // Calculate Y-axis domain from actual data including limits, properly handling null values
  const allTemps = [];
  
  // First collect all valid temperature values from all sensors and limits
  processedData.forEach(dataPoint => {
    // Add valid sensor values
    sensorKeys.forEach(key => {
      if (dataPoint[key] !== null && dataPoint[key] !== undefined && !isNaN(dataPoint[key])) {
        allTemps.push(dataPoint[key]);
      }
    });
    
    // Add valid limit values
    if (dataPoint.min_limit !== undefined && dataPoint.min_limit !== null && !isNaN(dataPoint.min_limit)) {
      allTemps.push(dataPoint.min_limit);
    }
    if (dataPoint.max_limit !== undefined && dataPoint.max_limit !== null && !isNaN(dataPoint.max_limit)) {
      allTemps.push(dataPoint.max_limit);
    }
  });
  
  // Calculate domain only if we have valid data
  let minTemp = 0;
  let maxTemp = 30;
  
  if (allTemps.length > 0) {
    minTemp = Math.floor(Math.min(...allTemps));
    maxTemp = Math.ceil(Math.max(...allTemps));
  }

  // IMPORTANT - Dynamically assign colors to all data series
  // This ensures consistency between chart and legend
  const MIN_LIMIT_COLOR = '#34A853'; // Green
  const MAX_LIMIT_COLOR = '#FF0000'; // Red
  
  // Create a color map to keep track of which color is assigned to each data series
  const colorMap = {};
  
  // Assign specific colors to min and max limits
  colorMap['min_limit'] = MIN_LIMIT_COLOR;
  colorMap['max_limit'] = MAX_LIMIT_COLOR;
  
  // Assign colors to sensors dynamically
  sensorKeys.forEach((sensor, index) => {
    colorMap[sensor] = COLORS[index % COLORS.length];
  });

  // Create legend payload with proper mapping between dataKeys and colors
  const legendPayload = [
    ...sensorKeys.map(key => ({ dataKey: key, name: key })),
    { dataKey: 'min_limit', name: 'Min Limit' },
    { dataKey: 'max_limit', name: 'Max Limit' }
  ];
  
  // Create colors array that matches the order of the legendPayload
  const legendColors = [
    ...sensorKeys.map(key => colorMap[key]),
    colorMap['min_limit'],
    colorMap['max_limit']
  ];

  // Calculate sensible Y-axis domain with good buffer
  const yMin = Math.max(0, minTemp - 2); // Never go below 0°C
  const yMax = maxTemp + 2;
  
  // Determine tick interval based on range
  const range = yMax - yMin;
  const tickInterval = range > 15 ? 5 : (range > 7 ? 2 : 1);
  
  // Create Y-axis ticks at appropriate intervals
  const yTicks = [];
  for (let i = Math.floor(yMin / tickInterval) * tickInterval; 
       i <= Math.ceil(yMax / tickInterval) * tickInterval; 
       i += tickInterval) {
    if (i >= yMin && i <= yMax) {
      yTicks.push(i);
    }
  }

  return (
    <div ref={chartRef}>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart 
          data={processedData} 
          margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="timestamp"
            tickFormatter={formatXAxis}
            ticks={oneDayTicks}
            tick={{ fontSize: 10 }}
          />
          <YAxis 
            domain={[yMin, yMax]} // Use calculated domain with buffer
            ticks={yTicks}     // Use calculated ticks
            label={{ 
              value: '°C',
              position: 'insideLeft',
              angle: -90,
              dx: 10
            }}
          />
          <Tooltip 
            content={() => null} 
            cursor={{ stroke: '#ccc', strokeWidth: 1 }} 
          />
          
          {/* Reference area for recommended temperature range */}
          {processedData[0]?.min_limit !== undefined && processedData[0]?.max_limit !== undefined && (
            <ReferenceArea
              y1={processedData[0].min_limit}
              y2={processedData[0].max_limit}
              fill="#4caf50"
              fillOpacity={0.1}
            />
          )}
          
          {/* Sensor lines - use the color from our colorMap */}
          {sensorKeys.map((sensor) => (
            <Line 
              key={sensor}
              type="monotone" 
              dataKey={sensor}
              name={sensor}
              stroke={colorMap[sensor]}
              dot={false}
              connectNulls={false} // Don't connect across null values to show gaps
            />
          ))}
          
          {/* Limit lines - use the specific colors we defined */}
          <Line 
            type="monotone" 
            dataKey="min_limit" 
            stroke={colorMap['min_limit']} 
            name="Min Limit"
            dot={false} 
            strokeDasharray="5 5"
            connectNulls={true} // Connect across nulls for limit lines
          />
          
          <Line 
            type="monotone" 
            dataKey="max_limit" 
            stroke={colorMap['max_limit']} 
            name="Max Limit"
            dot={false} 
            strokeDasharray="5 5"
            connectNulls={true} // Connect across nulls for limit lines
          />
        </LineChart>
      </ResponsiveContainer>
      
      {containerWidth > 0 && activeData && (
        <div className="mt-4">
          <CustomLegend 
            payload={legendPayload}
            colors={legendColors}
            colorMap={colorMap} // Pass the entire colorMap for direct lookup
            activeData={activeData}
            containerWidth={containerWidth}
            processedData={processedData}
            valueFormatter={(value) => {
              if (value === null || value === undefined) return 'N/A';
              const numValue = typeof value === 'number' ? value : parseFloat(value);
              return !isNaN(numValue) ? `${numValue.toFixed(1)}°C` : 'N/A';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TemperatureChart;